import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowRight, CheckCircle2, Plug, Users, CalendarClock } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { TeamRole } from '../types/supabase'
import { Button } from './ui/button'
import { WorkspaceNameRecovery } from './WorkspaceNameRecovery'
import { AgentConnectGrid } from './AgentConnectGrid'
import { ConnectAgentModal } from './ConnectAgentModal'
import { AddSlackMembersSheet } from './TeamManagement/AddSlackMembersSheet'
import { ScheduleConfiguration } from './ScheduleConfiguration'
import { SlackWorkspaceConnection } from './SlackWorkspaceConnection'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog'

type Progress = { step: number; mode: 'active' | 'later' | 'finished' }
const initialProgress: Progress = { step: 0, mode: 'active' }

function readProgress(key: string, lastStep: number): Progress {
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null')
    if (value && Number.isInteger(value.step) && value.step >= 0 && value.step <= lastStep
      && ['active', 'later', 'finished'].includes(value.mode)) return value
  } catch { /* The guide remains usable when browser storage is unavailable. */ }
  return initialProgress
}

export function OnboardingFlow({ teamId, teamName, role, standupsEnabled }: {
  teamId: string; teamName: string; role: TeamRole; standupsEnabled: boolean
}) {
  const { user } = useAuth()
  const managesWorkspace = role === 'owner' || role === 'admin'
  if (!user) return null
  return <SetupGuide key={`${user.id}:${teamId}:${managesWorkspace}`} teamId={teamId}
    teamName={teamName} authUserId={user.id} managesWorkspace={managesWorkspace} role={role}
    standupsEnabled={standupsEnabled} />
}

function SetupGuide({ teamId, teamName, authUserId, managesWorkspace, standupsEnabled, role }: {
  teamId: string; teamName: string; authUserId: string; managesWorkspace: boolean; standupsEnabled: boolean; role: TeamRole
}) {
  const [params, setParams] = useSearchParams()
  const lastStep = managesWorkspace ? 3 : 1
  const storageKey = `syncsimple:onboarding:v1:${authUserId}:${teamId}:${managesWorkspace ? 'workspace' : 'personal'}`
  const [progress, setProgress] = useState(() => readProgress(storageKey, lastStep))
  const [open, setOpen] = useState(() => params.get('setup') === 'resume' || readProgress(storageKey, lastStep).mode === 'active')
  const [connection, setConnection] = useState<boolean | null>(null)
  const [connectedTools, setConnectedTools] = useState<Set<string>>(() => new Set())
  const [hasMembers, setHasMembers] = useState<boolean | null>(null)
  const [checking, setChecking] = useState(false)
  const [checkError, setCheckError] = useState(false)
  const [retry, setRetry] = useState(0)
  const [connectingTool, setConnectingTool] = useState(false)
  const [slackConnected, setSlackConnected] = useState<boolean | null>(null)
  const [editingSchedule, setEditingSchedule] = useState(false)
  const [scheduleEnabled, setScheduleEnabled] = useState(standupsEnabled)
  const [saving, setSaving] = useState(false)
  const requiresSlack = role === 'owner'
  const slackBlocked = requiresSlack && slackConnected !== true
  const step = slackBlocked ? 0 : progress.step

  useEffect(() => {
    if (params.get('setup') !== 'resume') return
    if (progress.mode === 'finished') save(initialProgress)
    setOpen(true)
    setParams(previous => { const next = new URLSearchParams(previous); next.delete('setup'); return next }, { replace: true })
  }, [params, setParams])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setChecking(true)
    setCheckError(false)
    setConnection(null)
    setConnectedTools(new Set())
    setHasMembers(null)
    async function check() {
      try {
        const [profile, members] = await Promise.all([
          supabase.from('users').select('id').eq('team_id', teamId).eq('auth_user_id', authUserId).maybeSingle(),
          managesWorkspace
            ? supabase.from('team_members').select('id', { count: 'exact', head: true }).eq('team_id', teamId)
            : Promise.resolve({ count: null, error: null }),
        ])
        if (profile.error || members.error || !profile.data) throw new Error('Setup status unavailable')
        const agents = await supabase.from('agents').select('client_name').eq('team_id', teamId)
          .eq('user_id', profile.data.id).is('deleted_at', null)
        if (agents.error) throw agents.error
        if (!cancelled) {
          setConnection((agents.data?.length ?? 0) > 0)
          setConnectedTools(new Set((agents.data ?? []).flatMap(agent => agent.client_name ? [agent.client_name.toLowerCase()] : [])))
          setHasMembers(members.count === null ? null : members.count > 1)
        }
      } catch {
        if (!cancelled) setCheckError(true)
      } finally {
        if (!cancelled) setChecking(false)
      }
    }
    void check()
    return () => { cancelled = true }
  }, [open, teamId, authUserId, managesWorkspace, retry])

  function save(next: Progress) {
    setProgress(next)
    try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* Optional browser persistence. */ }
  }
  function pause() { if (saving || connectingTool || slackBlocked) return; save({ ...progress, mode: 'later' }); setOpen(false) }
  function advance() {
    if (saving || slackBlocked) return
    if (step === lastStep) {
      save({ step: lastStep, mode: 'finished' })
      setOpen(false)
    } else save({ step: step + 1, mode: 'active' })
  }
  const currentDone = step === 1 ? connection : step === 2 ? hasMembers : step === 3 ? scheduleEnabled : false
  const title = step === 0 ? 'Welcome to SyncSimple' : step === 1 ? 'Connect your tools'
    : step === 2 ? 'Invite team members' : 'Set up standups'
  const description = step === 0
    ? requiresSlack ? 'Install the SyncSimple Slack app first, then set up your own account and workspace.'
      : managesWorkspace ? 'Set up your own account, then get your workspace ready for the team.'
      : 'Get your own account ready. Your workspace owner manages shared team settings.'
    : step === 1 ? 'Connect the tool you use on your computer so your work can appear in SyncSimple.'
    : step === 2 ? 'Choose people from your Slack workspace. You can organize them into teams later.'
    : 'Standups are optional. Set a shared reminder schedule, summaries and Slack channel when your team needs them.'

  return <>
    {progress.mode !== 'finished' && <section aria-label="Getting started" className="flex min-w-0 flex-col gap-3 border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
      <div className="min-w-0">
        <h2 className="font-semibold text-foreground">Get started with SyncSimple</h2>
        <p className="mt-1 text-sm text-muted-foreground">{managesWorkspace ? 'Your tools, your team and optional standups.' : 'Connect your own tools and explore your workspace.'}</p>
      </div>
      <Button variant="outline" className="min-h-11 shrink-0" onClick={() => {
        setOpen(true)
      }}>Continue setup<ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /></Button>
    </section>}
    <Dialog open={open} onOpenChange={next => { if (!next) pause() }}>
      <DialogContent overlayClassName="bg-ink-900/50 backdrop-blur-sm" onInteractOutside={event => event.preventDefault()}
        showCloseButton={!slackBlocked} onEscapeKeyDown={event => { if (slackBlocked) event.preventDefault() }}
        className="flex max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl bg-card p-0 shadow-2xl sm:rounded-2xl [&>button]:right-5 [&>button]:top-5 [&>button]:flex [&>button]:min-h-11 [&>button]:min-w-11 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-full [&>button]:bg-muted [&>button]:opacity-100">
        <DialogHeader className="shrink-0 border-b border-border px-6 py-5 pr-20 text-left sm:px-8 sm:py-6 sm:pr-20">
          <DialogTitle className="break-words text-2xl font-semibold leading-tight tracking-tight">{title}</DialogTitle>
          <p className="mt-1 text-sm text-muted-foreground">Step {step + 1} of {lastStep + 1}</p>
          <DialogDescription className="sr-only">{description}</DialogDescription>
        </DialogHeader>
        <div role="progressbar" aria-label="Onboarding progress" aria-valuemin={1} aria-valuemax={lastStep + 1} aria-valuenow={step + 1} className="flex shrink-0 gap-1.5 px-6 py-4 sm:px-8">
          {Array.from({ length: lastStep + 1 }, (_, index) => <div key={index} aria-hidden="true" className={`h-1.5 flex-1 rounded-full ${index <= step ? 'bg-foreground' : 'bg-muted'}`} />)}
        </div>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 pb-6 pt-3 sm:px-8 sm:pb-8">
          <div className="flex items-start gap-4">
            <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${step === 0 ? 'rounded-md bg-signal-magenta text-paper-50' : step === 2 ? 'bg-signal-amber/10 text-signal-amber' : 'bg-primary/10 text-primary'}`}>
              {step === 0 ? <span className="text-lg font-semibold">{teamName.charAt(0).toUpperCase()}</span>
                : step === 1 ? <Plug aria-hidden="true" className="h-7 w-7" /> : step === 2 ? <Users aria-hidden="true" className="h-7 w-7" /> : <CalendarClock aria-hidden="true" className="h-7 w-7" />}
            </div>
            <div className="min-w-0">
              <h3 className="break-words text-lg font-semibold leading-snug">{step === 0 ? `Welcome, ${teamName}` : step === 1 ? 'Connect your own tool' : step === 2 ? 'Invite your team' : 'Standups are optional'}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{description}</p>
            </div>
          </div>
          {step === 0 && <WorkspaceNameRecovery name={teamName} />}
          {requiresSlack && <div hidden={step !== 0} className="space-y-2">
            <p className="text-sm font-semibold">Slack installation is required</p>
            <SlackWorkspaceConnection teamId={teamId} onStatusChange={setSlackConnected} requireFreshStatus canInstall />
            {slackBlocked && <p className="text-sm text-muted-foreground">Install the SyncSimple Slack app and verify the installation before continuing.</p>}
          </div>}
          {step === 0 ? <div className="border border-border bg-muted/40 p-5 sm:p-6">
            <h4 className="border-b border-border pb-3 font-semibold">What you&apos;ll set up:</h4>
            <ol className="mt-4 space-y-4">
              <GuideItem icon={Plug} title="Connect your own tool" text={managesWorkspace ? "Owners connect their personal tools too. Each teammate connects their own account." : "Install the connection on your computer. Your teammates connect their own accounts."} />
              {managesWorkspace ? <>
                <GuideItem icon={Users} title="Add team members" text="Choose who joins your workspace. Add or organize teams later." />
                <GuideItem icon={CalendarClock} title="Set up standups if you need them" text="Keep reminders off until you choose and save a schedule." />
              </> : <li className="text-sm leading-relaxed text-muted-foreground">Your owner manages shared settings and invitations. Your personal timezone is available in Settings.</li>}
            </ol>
          </div> : <>
            {checking && step !== 3 ? <p role="status" className="text-sm text-muted-foreground">Checking your existing setup...</p>
              : currentDone ? <p className="flex items-start gap-2 text-sm font-medium"><CheckCircle2 aria-hidden="true" className="h-5 w-5 shrink-0 text-primary" />{step === 1 ? 'Your connector is already recorded.' : step === 2 ? 'Other members have already joined this workspace.' : 'Standups are already enabled.'}</p> : null}
            {checkError && step !== 3 && <p role="alert" className="text-sm text-muted-foreground">We could not verify your existing setup. You can still set up below or try again.<Button variant="link" className="min-h-11 px-2" onClick={() => setRetry(value => value + 1)}>Try again</Button></p>}
            {step === 1 && <AgentConnectGrid connectedClientNames={connectedTools} onConnect={() => setConnectingTool(true)} />}
            {step === 2 && managesWorkspace && <div className="space-y-5">
              {!requiresSlack && <SlackWorkspaceConnection teamId={teamId} onStatusChange={setSlackConnected} />}
              {slackConnected === true && <AddSlackMembersSheet teamId={teamId} initialPodId="" pods={[]} isOwner={role === 'owner'} workspaceOnly
                onClose={() => setRetry(value => value + 1)} onSaved={async () => { setRetry(value => value + 1) }} onSavingChange={setSaving} />}
              <p className="text-xs text-muted-foreground">Use Add members to save your selection before continuing. Adding people does not grant ownership.</p>
            </div>}
            {step === 3 && managesWorkspace && (editingSchedule
              ? <ScheduleConfiguration teamId={teamId} userRole={role} onSavingChange={setSaving} onSaved={config => setScheduleEnabled(config.enabled || config.digest_enabled)} />
              : <div className="space-y-4"><p className="text-sm leading-relaxed text-muted-foreground">Choose a schedule, AI summaries and a Slack channel here, or finish setup and return to standups later.</p><Button className="min-h-11" onClick={() => setEditingSchedule(true)}>{scheduleEnabled ? 'Manage standups' : 'Set up standups'}</Button></div>)}
            {step === 3 && editingSchedule && <p className="text-xs text-muted-foreground">Save your schedule above before completing setup. Continuing does not save or enable standups.</p>}
          </>}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border px-6 py-5 sm:px-8 sm:py-6">
          {step > 0 ? <Button variant="outline" disabled={saving} className="min-h-11" onClick={() => save({ step: step - 1, mode: 'active' })}>Go Back</Button>
            : <Button variant="outline" disabled={slackBlocked} className="min-h-11" onClick={pause}>Skip Setup</Button>}
          <Button disabled={saving || slackBlocked} className="min-h-11" onClick={advance}>
            {saving ? 'Saving...' : step === lastStep ? 'Complete Setup' : 'Next'}
            <ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    {open && connectingTool && <ConnectAgentModal isOpen teamId={teamId} onClose={() => setConnectingTool(false)} onConnected={() => { setConnection(true); setRetry(value => value + 1) }} />}

  </>
}

function GuideItem({ icon: Icon, title, text }: { icon: typeof Plug; title: string; text: string }) {
  return <li className="flex gap-3"><Icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><div><p className="text-sm font-semibold">{title}</p><p className="mt-1 text-sm leading-relaxed text-muted-foreground">{text}</p></div></li>
}

export function OnboardingReturn({ teamId }: { teamId: string }) {
  const [params] = useSearchParams()
  if (params.get('setup') !== '1' && params.get('slack') !== 'connected') return null
  return <aside aria-label="Onboarding navigation" className="flex shrink-0 flex-col gap-3 border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
    <p className="text-sm text-muted-foreground">When you are ready, return to your setup guide. Your saved changes stay here.</p>
    <Button asChild variant="outline" className="min-h-11 shrink-0"><Link to={`/dashboard/team/${teamId}?setup=resume`}>Continue onboarding<ArrowRight aria-hidden="true" className="ml-2 h-4 w-4" /></Link></Button>
  </aside>
}
